/**
 * M7 Operations & Tracking REST client.
 *
 * Mirrors `/api/v1/operations/...` and the M7 additions to `/api/v1/mobile/...`.
 * Reuses the auth-store access token + ApiError envelope from the auth feature.
 */

import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

// ── Types ────────────────────────────────────────────────────────────────
export type AssetStatus = 'ACTIVE' | 'IDLE' | 'ALERT' | 'OFFLINE' | 'STALE' | 'MAINTENANCE';

export type AssetRegistry = {
  id: string;
  code: string;
  name: string;
  asset_type_slug: string;
  plate_number: string | null;
  photo_url: string | null;
  status: AssetStatus;
  assigned_field_user_id: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  fuel_type: string | null;
  capacity_value: number | null;
  capacity_unit: string | null;
  purchase_date: string | null;
  purchase_price_minor: number | null;
  currency: string | null;
  odometer_current: number | null;
  notes: string | null;
  deleted_at: string | null;
  last_fix_at: string | null;
  lat: number | null;
  lng: number | null;
};

export type AssetListResponse = {
  total: number;
  items: AssetRegistry[];
};

export type AssetType = {
  id: string;
  slug: string;
  name: string;
  icon: string;
  is_enabled: boolean;
  required_document_slugs: string[];
  metadata_schema: Record<string, unknown>;
};

export type DocumentType = {
  id: string;
  slug: string;
  name: string;
  scope: 'ASSET' | 'FIELD_USER';
  is_required: boolean;
  is_enabled: boolean;
  expiry_lead_days: number[];
};

export type DocumentDto = {
  id: string;
  document_type_id: string;
  document_type_slug: string | null;
  scope: 'ASSET' | 'FIELD_USER';
  asset_id: string | null;
  field_user_id: string | null;
  title: string;
  mime_type: string;
  size_bytes: number;
  issued_on: string | null;
  expires_at: string | null;
  uploaded_by_user_id: string | null;
  notes: string | null;
  deleted_at: string | null;
  status_color: 'ok' | 'warning' | 'expiring' | 'expired';
  download_url: string | null;
};

export type FuelLog = {
  id: string;
  asset_id: string;
  field_user_id: string | null;
  duty_session_id: string | null;
  logged_at: string;
  liters: number;
  cost_minor: number;
  currency: string;
  odometer: number | null;
  meter_photo_path: string | null;
  receipt_photo_path: string | null;
  latitude: number | null;
  longitude: number | null;
  is_anomaly: boolean;
  anomaly_reason: string | null;
  notes: string | null;
  verified_at: string | null;
  verified_by_user_id: string | null;
};

export type FuelCostRollupRow = {
  asset_id: string;
  total_cost_minor: number;
  total_liters: number;
  distance_km: number;
  cost_per_km_minor: number;
};

export type FuelCostRollup = {
  start: string;
  end: string;
  rows: FuelCostRollupRow[];
};

export type FuelPhotoUrl = {
  kind: 'receipt' | 'meter';
  url: string;
  expires_in_seconds: number;
};

export type MaintenanceTicket = {
  id: string;
  asset_id: string;
  field_user_id: string | null;
  duty_session_id: string | null;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  title: string;
  description: string;
  status: 'OPEN' | 'TRIAGED' | 'CONVERTED' | 'REJECTED' | 'CLOSED';
  raised_at: string;
  photo_paths: string[];
  voice_path: string | null;
  video_path: string | null;
  triaged_at: string | null;
  triaged_by_user_id: string | null;
  assigned_to_user_id: string | null;
  rejection_reason: string | null;
  work_order_id: string | null;
};

export type WorkOrder = {
  id: string;
  asset_id: string;
  title: string;
  description: string | null;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'AWAITING_PARTS' | 'COMPLETED' | 'CANCELLED';
  is_preventive: boolean;
  due_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  parts_cost_minor: number;
  labor_cost_minor: number;
  currency: string;
  assigned_to_user_id: string | null;
  vendor_name: string | null;
  odometer_at_service: number | null;
  notes: string | null;
};

export type EngagementDetail = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ARCHIVED';
  site_latitude: number | null;
  site_longitude: number | null;
  site_radius_m: number | null;
  customer_name: string | null;
  customer_contact: string | null;
  starts_on: string | null;
  ends_on: string | null;
  billing_unit: string | null;
  billing_rate_minor: number | null;
  currency: string | null;
  expected_work_unit_minutes: number | null;
  auto_geofence: boolean;
};

export type EngagementAssignment = {
  id: string;
  engagement_id: string;
  field_user_id: string;
  asset_id: string | null;
  starts_on: string | null;
  ends_on: string | null;
  role_label: string | null;
  is_active: boolean;
};

export type DutySession = {
  id: string;
  field_user_id: string;
  asset_id: string;
  started_at: string;
  ended_at: string | null;
  start_odometer: number;
  end_odometer: number | null;
  start_odometer_photo_path: string | null;
  end_odometer_photo_path: string | null;
  started_without_gps: boolean;
  status: 'ACTIVE' | 'ENDED' | 'CANCELLED';
  distance_m: number;
  work_unit_count: number;
};

export type MediaSignedUrl = {
  url: string;
  expires_in_seconds: number;
};

export type WorkUnit = {
  id: string;
  duty_session_id: string;
  engagement_id: string | null;
  started_at: string;
  ended_at: string | null;
  reference_number: string | null;
  proof_paths: { items?: string[]; kind?: string } & Record<string, unknown>;
  cancel_reason: string | null;
  status: 'ACTIVE' | 'ENDED' | 'CANCELLED';
  auto_closed: boolean;
};

export type AdvanceDto = {
  id: string;
  field_user_id: string;
  amount_minor: number;
  currency: string;
  reason: string | null;
  status: 'REQUESTED' | 'APPROVED' | 'DISBURSED' | 'REJECTED' | 'DEDUCTED';
  requested_at: string;
  approved_at: string | null;
  approved_by_user_id: string | null;
  disbursed_at: string | null;
  rejection_reason: string | null;
};

export type AttendanceRow = {
  id: string;
  field_user_id: string;
  work_date: string;
  status: 'PRESENT' | 'PARTIAL' | 'ABSENT' | 'LEAVE' | 'HOLIDAY' | 'WEEKEND';
  worked_hours: number;
  regular_hours: number;
  overtime_hours: number;
  overtime_multiplier_used: number | null;
  first_in_at: string | null;
  last_out_at: string | null;
  corrected_by_user_id: string | null;
  correction_note: string | null;
  is_manual: boolean;
};

export type LeaveType = 'ANNUAL' | 'SICK' | 'UNPAID' | 'EMERGENCY' | 'OTHER';

export type LeaveRequest = {
  id: string;
  field_user_id: string;
  leave_type: LeaveType;
  starts_on: string;
  ends_on: string;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  decided_at: string | null;
  decided_by_user_id: string | null;
  decision_note: string | null;
};

export type ShiftDefinition = {
  id: string;
  slug: string;
  name: string;
  start_time: string;
  end_time: string;
  standard_hours: number;
  is_enabled: boolean;
  days_of_week: number[];
};

export type OvertimeRule = {
  id: string;
  name: string;
  is_enabled: boolean;
  priority: number;
  daily_threshold_hours: number | null;
  weekly_threshold_hours: number | null;
  multiplier: number;
  applies_on_days: number[];
};

export type AssetDetail = {
  asset: AssetRegistry;
  documents: DocumentDto[];
  service_history: WorkOrder[];
  open_tickets: MaintenanceTicket[];
  open_alerts: number;
  today_distance_m: number;
  total_fuel_minor_30d: number;
  total_fuel_liters_30d: number;
};

export type DocumentUploadTicket = {
  document_id: string;
  object_path: string;
  upload_url: string;
  expires_in_seconds: number;
};

// ── HTTP helper ──────────────────────────────────────────────────────────
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

// ── Endpoints ────────────────────────────────────────────────────────────
export const operationsApi = {
  // Assets
  listAssets: (
    params: {
      search?: string;
      asset_type_slug?: string;
      status?: string;
      include_deleted?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return call<AssetListResponse>(`/operations/assets${qs ? `?${qs}` : ''}`);
  },
  getAsset: (id: string) => call<AssetDetail>(`/operations/assets/${id}`),
  createAsset: (body: Partial<AssetRegistry> & { code: string; name: string }) =>
    call<AssetRegistry>('/operations/assets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchAsset: (id: string, body: Partial<AssetRegistry>) =>
    call<AssetRegistry>(`/operations/assets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAsset: (id: string) =>
    call<AssetRegistry>(`/operations/assets/${id}`, { method: 'DELETE' }),
  restoreAsset: (id: string) =>
    call<AssetRegistry>(`/operations/assets/${id}:restore`, { method: 'POST' }),
  exportAssetsCsv: async () => {
    const token = useAuthStore.getState().accessToken;
    const res = await fetch(`${env.VITE_API_URL}/api/v1/operations/assets.csv`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.blob();
  },

  // Asset types
  listAssetTypes: () => call<AssetType[]>('/operations/asset-types'),
  createAssetType: (body: Omit<AssetType, 'id'>) =>
    call<AssetType>('/operations/asset-types', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchAssetType: (id: string, body: Partial<AssetType>) =>
    call<AssetType>(`/operations/asset-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAssetType: (id: string) =>
    call<void>(`/operations/asset-types/${id}`, { method: 'DELETE' }),

  // Document types
  listDocumentTypes: (scope?: 'ASSET' | 'FIELD_USER') => {
    const q = scope ? `?scope=${scope}` : '';
    return call<DocumentType[]>(`/operations/document-types${q}`);
  },
  createDocumentType: (body: Omit<DocumentType, 'id'>) =>
    call<DocumentType>('/operations/document-types', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchDocumentType: (id: string, body: Partial<DocumentType>) =>
    call<DocumentType>(`/operations/document-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Documents
  beginDocumentUpload: (body: {
    document_type_id: string;
    scope: 'ASSET' | 'FIELD_USER';
    asset_id?: string;
    field_user_id?: string;
    title: string;
    mime_type: string;
    size_bytes: number;
    issued_on?: string | null;
    expires_at?: string | null;
    notes?: string | null;
  }) =>
    call<DocumentUploadTicket>('/operations/documents:upload-start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listFieldUserDocuments: (fieldUserId: string) =>
    call<DocumentDto[]>(`/operations/documents/by-field-user/${fieldUserId}`),
  getDocument: (id: string) => call<DocumentDto>(`/operations/documents/${id}`),
  deleteDocument: (id: string) =>
    call<DocumentDto>(`/operations/documents/${id}`, { method: 'DELETE' }),

  // Fuel
  createFuelLog: (body: Partial<FuelLog>) =>
    call<FuelLog>('/operations/fuel-logs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listFuelForAsset: (assetId: string, params: { start?: string; end?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.start) q.set('start', params.start);
    if (params.end) q.set('end', params.end);
    return call<FuelLog[]>(
      `/operations/fuel-logs/by-asset/${assetId}${q.toString() ? `?${q}` : ''}`,
    );
  },
  listFuelLogs: (
    params: {
      start?: string;
      end?: string;
      anomaly_only?: boolean;
      unverified_only?: boolean;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return call<FuelLog[]>(`/operations/fuel-logs${qs ? `?${qs}` : ''}`);
  },
  verifyFuelLog: (id: string) =>
    call<FuelLog>(`/operations/fuel-logs/${id}:verify`, { method: 'POST' }),
  fuelPhotoUrl: (id: string, kind: 'receipt' | 'meter' = 'receipt') =>
    call<FuelPhotoUrl>(`/operations/fuel-logs/${id}/photo?kind=${kind}`),
  fuelCostPerAsset: (
    params: { days?: number; asset_id?: string; from?: string; to?: string } = {},
  ) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return call<FuelCostRollup>(`/operations/fuel/cost-per-asset${qs ? `?${qs}` : ''}`);
  },

  // Maintenance
  listTickets: (assetId?: string) => {
    const q = assetId ? `?asset_id=${assetId}` : '';
    return call<MaintenanceTicket[]>(`/operations/maintenance/tickets${q}`);
  },
  createTicket: (body: {
    asset_id: string;
    severity: MaintenanceTicket['severity'];
    title: string;
    description: string;
    raised_at: string;
    photo_paths?: string[];
  }) =>
    call<MaintenanceTicket>('/operations/maintenance/tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  triageTicket: (
    id: string,
    body: {
      decision: 'TRIAGE' | 'REJECT';
      rejection_reason?: string;
      severity?: MaintenanceTicket['severity'];
      assigned_to_user_id?: string;
    },
  ) =>
    call<MaintenanceTicket>(`/operations/maintenance/tickets/${id}:triage`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listWorkOrders: (assetId?: string) => {
    const q = assetId ? `?asset_id=${assetId}` : '';
    return call<WorkOrder[]>(`/operations/maintenance/work-orders${q}`);
  },
  createWorkOrder: (body: {
    asset_id: string;
    title: string;
    description?: string;
    is_preventive?: boolean;
    due_at?: string | null;
    from_ticket_id?: string;
    assigned_to_user_id?: string;
    vendor_name?: string;
    parts_cost_minor?: number;
    labor_cost_minor?: number;
    currency?: string;
    notes?: string;
  }) =>
    call<WorkOrder>('/operations/maintenance/work-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchWorkOrder: (id: string, body: Partial<WorkOrder>) =>
    call<WorkOrder>(`/operations/maintenance/work-orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Engagements
  listEngagements: () => call<EngagementDetail[]>('/operations/engagements'),
  getEngagement: (id: string) => call<EngagementDetail>(`/operations/engagements/${id}`),
  createEngagement: (body: Partial<EngagementDetail> & { code: string; name: string }) =>
    call<EngagementDetail>('/operations/engagements', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchEngagement: (id: string, body: Partial<EngagementDetail>) =>
    call<EngagementDetail>(`/operations/engagements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  listAssignments: (engagementId: string) =>
    call<EngagementAssignment[]>(`/operations/engagements/${engagementId}/assignments`),
  createAssignment: (body: {
    engagement_id: string;
    field_user_id: string;
    asset_id?: string;
    role_label?: string;
  }) =>
    call<EngagementAssignment>('/operations/engagements/assignments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deactivateAssignment: (id: string) =>
    call<EngagementAssignment>(`/operations/engagements/assignments/${id}`, {
      method: 'DELETE',
    }),

  // Advances
  listPendingAdvances: () => call<AdvanceDto[]>('/operations/advances/pending'),
  listAdvancesForUser: (userId: string) =>
    call<AdvanceDto[]>(`/operations/advances/by-user/${userId}`),
  decideAdvance: (
    id: string,
    body: { decision: 'APPROVE' | 'REJECT'; rejection_reason?: string },
  ) =>
    call<AdvanceDto>(`/operations/advances/${id}:decide`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  markDisbursed: (id: string) =>
    call<AdvanceDto>(`/operations/advances/${id}:mark-disbursed`, {
      method: 'POST',
    }),

  // Attendance + leave
  listAttendance: (userId: string, params: { start: string; end: string }) => {
    const q = new URLSearchParams({ start: params.start, end: params.end });
    return call<AttendanceRow[]>(`/operations/attendance/by-user/${userId}?${q.toString()}`);
  },
  correctAttendance: (
    userId: string,
    body: {
      work_date: string;
      status: AttendanceRow['status'];
      worked_hours: number;
      overtime_hours?: number;
      correction_note: string;
      first_in_at?: string | null;
      last_out_at?: string | null;
      is_manual?: boolean;
    },
  ) =>
    call<AttendanceRow>(`/operations/attendance/by-user/${userId}:correct`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Work units
  listWorkUnitsForUser: (userId: string, limit = 100) =>
    call<WorkUnit[]>(`/operations/work-units/by-user/${userId}?limit=${limit}`),

  // Duty sessions
  listDutySessionsForUser: (userId: string, limit = 20) =>
    call<DutySession[]>(`/operations/duty-sessions/by-user/${userId}?limit=${limit}`),

  // Capture-media signing (odometer / fuel / ticket / proof photos)
  signMedia: (path: string) =>
    call<MediaSignedUrl>(`/operations/media:sign?path=${encodeURIComponent(path)}`),

  listPendingLeave: () => call<LeaveRequest[]>('/operations/leave/pending'),
  createLeave: (body: {
    field_user_id: string;
    leave_type: LeaveType;
    starts_on: string;
    ends_on: string;
    reason?: string | null;
  }) =>
    call<LeaveRequest>('/operations/leave', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  decideLeave: (id: string, body: { decision: 'APPROVE' | 'REJECT'; note?: string }) =>
    call<LeaveRequest>(`/operations/leave/${id}:decide`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Shifts + overtime rules (attendance settings)
  listShifts: () => call<ShiftDefinition[]>('/operations/shifts'),
  createShift: (body: {
    slug: string;
    name: string;
    start_time: string;
    end_time: string;
    standard_hours?: number;
    is_enabled?: boolean;
    days_of_week?: number[];
  }) =>
    call<ShiftDefinition>('/operations/shifts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listOvertimeRules: () => call<OvertimeRule[]>('/operations/overtime-rules'),
  createOvertimeRule: (body: {
    name: string;
    is_enabled?: boolean;
    priority?: number;
    daily_threshold_hours?: number | null;
    weekly_threshold_hours?: number | null;
    multiplier?: number;
    applies_on_days?: number[];
  }) =>
    call<OvertimeRule>('/operations/overtime-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export type OperationsApi = typeof operationsApi;
